import { Button, Divider, Grid, Group, Modal, Paper, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Attachment, Composition, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { withTenantScope } from '../../utils/tenant';

interface AttachmentViewerProps {
  attachment: Attachment;
}

function AttachmentViewer({ attachment }: AttachmentViewerProps): JSX.Element {
  return (
    <iframe
      src={attachment.url}
      title={attachment.title ?? 'Attachment'}
      style={{ width: '100%', height: '70vh', border: 'none' }}
    />
  );
}

interface UploadReviewPanelProps {
  title: string;
  attachment: Attachment;
  questionnaireName: string;
  patientReference: string;
  compositionId: string;
}

export function UploadReviewPanel({
  title,
  attachment,
  questionnaireName,
  patientReference,
  compositionId,
}: UploadReviewPanelProps): JSX.Element {
  const medplum = useMedplum();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const pendingResponse = useRef<QuestionnaireResponse | null>(null);

  useEffect(() => {
    medplum
      .searchResources('Questionnaire', { name: questionnaireName })
      .then((results) => setQuestionnaire(results[0]))
      .catch(console.error)
      .finally(() => setLoadingQuestionnaire(false));
  }, [medplum, questionnaireName]);

  const handleFormSubmit = (response: QuestionnaireResponse): void => {
    pendingResponse.current = response;
    openConfirm();
  };

  const handleConfirm = (): void => {
    const response = pendingResponse.current;
    if (!response) {
      return;
    }
    closeConfirm();
    medplum
      .createResource(withTenantScope(response))
      .then(async (newQr) => {
        const composition = await medplum.readResource('Composition', compositionId);
        const updated: Composition = {
          ...composition,
          section: [...(composition.section ?? []), { entry: [{ reference: `QuestionnaireResponse/${newQr.id}` }] }],
        };
        await medplum.updateResource(updated);
        setSubmitted(true);
        showNotification({ color: 'green', message: `${title} response saved successfully.` });
      })
      .catch((error) => {
        showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
      });
  };

  return (
    <>
      <Modal opened={confirmOpen} onClose={closeConfirm} title="Confirm submission" centered>
        <Text size="sm" mb="lg">
          This action cannot be undone. Are you sure you want to submit the <strong>{title}</strong> results?
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={closeConfirm}>
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirm}>
            Submit
          </Button>
        </Group>
      </Modal>

      <Paper withBorder radius="md" p="md">
        <Title order={4} mb="md">
          Review Uploaded Results: {title}
        </Title>
        <Divider mb="md" />
        <Grid gutter="lg">
          <Grid.Col span={8}>
            <Title order={6} mb="xs" c="dimmed">
              Uploaded Document{attachment.title ? ` — ${attachment.title}` : ''}
            </Title>
            <AttachmentViewer attachment={attachment} />
          </Grid.Col>

          <Grid.Col span={4}>
            <Title order={6} mb="xs" c="dimmed">
              Fill in Results
            </Title>
            {loadingQuestionnaire && (
              <Text c="dimmed" size="sm">
                Loading questionnaire…
              </Text>
            )}
            {!loadingQuestionnaire && !questionnaire && (
              <Text c="red" size="sm">
                Questionnaire not found.
              </Text>
            )}
            {submitted && (
              <Text c="green" size="sm">
                Response saved successfully.
              </Text>
            )}
            {!submitted && questionnaire && (
              <QuestionnaireForm
                questionnaire={questionnaire}
                subject={{ reference: patientReference }}
                onSubmit={handleFormSubmit}
              />
            )}
          </Grid.Col>
        </Grid>
      </Paper>
    </>
  );
}
